/**
 * The Published Track Result — the result published for a track by the
 * person who holds the ball (owner ruling 2026-10-15; migration 934; canon 05
 * §11; glossary "Published Track Result").
 *
 * Closing a session publishes nothing for the track. The session's result
 * goes back into Catchball, where the ball holder decides whether to publish
 * it for the track. The server keeps one append-only `track_results` row per
 * act; the newest row is the track's answer. It feeds the Plan of Record input
 * and what goes up through Catchball.
 *
 * Strategies (canon 05 §11): latest_published / selected_official publish one
 * session; aggregate ("combined") averages the ranks of every closed session
 * that ranked the same items.
 *
 * Glossary 2026-10-14: a session's chosen result is "published"; "official"
 * means only a workspace's accepted value. The schema columns keep their old
 * names (tracks.official_session_id).
 */
import { useCallback, useEffect, useState } from 'react';
import { getTypedSupabaseClient } from '../typedSupabaseClient';

export type TrackResultStrategy = 'latest_published' | 'selected_official' | 'aggregate';

export interface PublishedTrackResult {
  trackId: string;
  /** The one session the result came from; null for a combined result or when nothing is published. */
  sessionId: string | null;
  sessionName: string | null;
  sessionReportId: string | null;
  publishedAt: string | null;
  /** The newest `track_results` row, or null when nothing is published for the track. */
  trackResultId: string | null;
  strategy: TrackResultStrategy | null;
  aggregationMethod: 'average_rank' | null;
  sourceSessionIds: string[];
  sourceCount: number;
  itemCount: number;
  cutLinePosition: number | null;
  /** Sessions on the track not combined into the result, and why. */
  leftOut: Array<{ sessionId: string; sessionName: string | null; reason: string }>;
  /** Closed sessions newer than the published result that the ball holder has not acted on. */
  waitingCount: number;
  latestClosedSessionId: string | null;
  latestClosedSessionName: string | null;
  publishedBy: string | null;
  publishedByName: string | null;
  /** The workflow lets a track choose its own rule (canon 05 allow_track_resolution_override). */
  trackOverrideAllowed: boolean;
  writeBack: string | null;
  /** The signed-in person may publish for this track (ball holder, cycle owner, admin). */
  mayPublish: boolean;
  /** Plain words: why this result (or why none). */
  reason: string;
}

/** untyped until db:types after prod apply (mig 934) */
interface Row {
  track_id?: string;
  session_id?: string | null;
  session_name?: string | null;
  session_report_id?: string | null;
  published_at?: string | null;
  track_result_id?: string | null;
  strategy?: string | null;
  aggregation_method?: string | null;
  source_session_ids?: unknown;
  source_count?: number | null;
  item_count?: number | null;
  cut_line_position?: number | null;
  left_out?: unknown;
  waiting_count?: number | null;
  latest_closed_session_id?: string | null;
  latest_closed_session_name?: string | null;
  published_by?: string | null;
  published_by_name?: string | null;
  track_override_allowed?: boolean | null;
  write_back?: string | null;
  may_publish?: boolean | null;
  reason?: string | null;
}

function strategyOf(v: unknown): TrackResultStrategy | null {
  return v === 'latest_published' || v === 'selected_official' || v === 'aggregate' ? v : null;
}

function uuidList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

export function parsePublishedTrackResults(data: unknown): PublishedTrackResult[] {
  if (!Array.isArray(data)) return [];
  return (data as Row[])
    .filter((r) => r && typeof r.track_id === 'string')
    .map((r) => ({
      trackId: r.track_id as string,
      sessionId: r.session_id ?? null,
      sessionName: r.session_name ?? null,
      sessionReportId: r.session_report_id ?? null,
      publishedAt: r.published_at ?? null,
      trackResultId: r.track_result_id ?? null,
      strategy: strategyOf(r.strategy),
      aggregationMethod: r.aggregation_method === 'average_rank' ? 'average_rank' : null,
      sourceSessionIds: uuidList(r.source_session_ids),
      sourceCount: typeof r.source_count === 'number' ? r.source_count : uuidList(r.source_session_ids).length,
      itemCount: typeof r.item_count === 'number' ? r.item_count : 0,
      cutLinePosition: typeof r.cut_line_position === 'number' ? r.cut_line_position : null,
      leftOut: Array.isArray(r.left_out)
        ? (r.left_out as Array<Record<string, unknown>>).map((x) => ({
            sessionId: String(x.session_id ?? ''),
            sessionName: typeof x.session_name === 'string' ? x.session_name : null,
            reason: typeof x.reason === 'string' ? x.reason : '',
          }))
        : [],
      waitingCount: typeof r.waiting_count === 'number' ? r.waiting_count : 0,
      latestClosedSessionId: r.latest_closed_session_id ?? null,
      latestClosedSessionName: r.latest_closed_session_name ?? null,
      publishedBy: r.published_by ?? null,
      publishedByName: r.published_by_name ?? null,
      trackOverrideAllowed: r.track_override_allowed === true,
      writeBack: r.write_back ?? null,
      mayPublish: r.may_publish === true,
      reason: r.reason ?? '',
    }));
}

/**
 * One line for a track, in the server's own words: "Published result: <session>,
 * published for the track by …" — or, for a combined result, the server's
 * reason already names the sources and the method.
 */
export function publishedResultLine(r: PublishedTrackResult): string {
  if (!r.trackResultId) return `No published result: ${r.reason}.`;
  if (r.strategy === 'aggregate') return `Published result: ${r.reason}.`;
  return `Published result: ${r.sessionName ?? 'Untitled session'}, ${r.reason}.`;
}

type Rpc = (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

function rpcOf(): Rpc | null {
  try {
    // Cast the client before binding: binding the typed rpc first makes TypeScript
    // expand every database function's signature (TS2589).
    const client = getTypedSupabaseClient() as unknown as { rpc: Rpc };
    return client.rpc.bind(client);
  } catch {
    return null;
  }
}

export async function fetchPublishedTrackResults(trackIds: string[]): Promise<Map<string, PublishedTrackResult>> {
  const out = new Map<string, PublishedTrackResult>();
  const ids = [...new Set(trackIds.filter(Boolean))];
  if (ids.length === 0) return out;
  const rpc = rpcOf();
  if (!rpc) return out; // No client (offline / tests): callers keep their unfiltered behavior.
  const { data, error } = await rpc('get_published_track_results', { p_track_ids: ids });
  if (error) return out;
  for (const r of parsePublishedTrackResults(data)) out.set(r.trackId, r);
  return out;
}

/** The published result for one track; refetches when `refreshKey` changes. */
export function usePublishedTrackResult(trackId: string | null | undefined, refreshKey?: unknown) {
  const [result, setResult] = useState<PublishedTrackResult | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!trackId) return;
    let cancelled = false;
    void fetchPublishedTrackResults([trackId]).then((m) => {
      if (!cancelled) setResult(m.get(trackId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [trackId, refreshKey, tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { result, refresh } as const;
}

// ── The result itself: the newest track_results row ─────────────────────────

export interface TrackResultItem {
  itemId: string;
  rank: number;
  score: number | null;
  /** Combined results only: the mean rank across the sources, and each source's rank. */
  averageRank: number | null;
  sessionRanks: Record<string, number>;
}

export interface TrackResultRecord {
  id: string;
  trackId: string;
  cycleId: string | null;
  strategy: TrackResultStrategy;
  aggregationMethod: 'average_rank' | null;
  sourceSessionIds: string[];
  leftOut: Array<{ sessionId: string; sessionName: string | null; reason: string }>;
  items: TrackResultItem[];
  itemCount: number;
  cutLinePosition: number | null;
  publishedBy: string;
  publishedByName: string | null;
  publishedAt: string;
  note: string | null;
  supersedesId: string | null;
}

interface TrackResultRow {
  id: string;
  track_id: string;
  cycle_id: string | null;
  strategy: string;
  aggregation_method: string | null;
  source_session_ids: unknown;
  left_out: unknown;
  ranked_items: unknown;
  item_count: number;
  cut_line_position: number | null;
  published_by: string;
  published_by_name: string | null;
  published_at: string;
  note: string | null;
  supersedes_id: string | null;
}

export function parseTrackResultRow(row: TrackResultRow): TrackResultRecord {
  const ranked = Array.isArray(row.ranked_items) ? (row.ranked_items as Array<Record<string, unknown>>) : [];
  return {
    id: row.id,
    trackId: row.track_id,
    cycleId: row.cycle_id ?? null,
    strategy: strategyOf(row.strategy) ?? 'latest_published',
    aggregationMethod: row.aggregation_method === 'average_rank' ? 'average_rank' : null,
    sourceSessionIds: uuidList(row.source_session_ids),
    leftOut: Array.isArray(row.left_out)
      ? (row.left_out as Array<Record<string, unknown>>).map((x) => ({
          sessionId: String(x.session_id ?? ''),
          sessionName: typeof x.session_name === 'string' ? x.session_name : null,
          reason: typeof x.reason === 'string' ? x.reason : '',
        }))
      : [],
    items: ranked
      .map((x) => ({
        itemId: String(x.item_id ?? ''),
        rank: Number(x.rank ?? 0),
        score: typeof x.score === 'number' ? x.score : null,
        averageRank: typeof x.average_rank === 'number' ? x.average_rank : null,
        sessionRanks:
          x.session_ranks && typeof x.session_ranks === 'object'
            ? Object.fromEntries(Object.entries(x.session_ranks as Record<string, unknown>).map(([k, v]) => [k, Number(v)]))
            : {},
      }))
      .filter((x) => x.itemId)
      .sort((a, b) => a.rank - b.rank),
    itemCount: row.item_count,
    cutLinePosition: row.cut_line_position ?? null,
    publishedBy: row.published_by,
    publishedByName: row.published_by_name ?? null,
    publishedAt: row.published_at,
    note: row.note ?? null,
    supersedesId: row.supersedes_id ?? null,
  };
}

const TRACK_RESULT_COLUMNS =
  'id, track_id, cycle_id, strategy, aggregation_method, source_session_ids, left_out, ranked_items, item_count, cut_line_position, published_by, published_by_name, published_at, note, supersedes_id';

type UntypedFrom = (table: string) => {
  select: (cols: string) => {
    eq: (col: string, v: string) => {
      order: (col: string, o: { ascending: boolean }) => {
        limit: (n: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
      };
    };
    in: (col: string, v: string[]) => {
      order: (col: string, o: { ascending: boolean }) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
    };
  };
};

function fromOf(): UntypedFrom | null {
  try {
    const client = getTypedSupabaseClient() as unknown as { from: UntypedFrom };
    return client.from.bind(client);
  } catch {
    return null;
  }
}

/** The newest result published for a track (RLS: members of the workspace). */
export async function fetchCurrentTrackResult(trackId: string): Promise<TrackResultRecord | null> {
  const from = fromOf();
  if (!from || !trackId) return null;
  const { data, error } = await from('track_results')
    .select(TRACK_RESULT_COLUMNS)
    .eq('track_id', trackId)
    .order('published_at', { ascending: false })
    .limit(1);
  if (error || !Array.isArray(data) || data.length === 0) return null;
  return parseTrackResultRow(data[0] as TrackResultRow);
}

/** Every result published for a track, newest first — the record of each act. */
export async function fetchTrackResultHistory(trackId: string): Promise<TrackResultRecord[]> {
  const from = fromOf();
  if (!from || !trackId) return [];
  const { data, error } = await from('track_results')
    .select(TRACK_RESULT_COLUMNS)
    .eq('track_id', trackId)
    .order('published_at', { ascending: false })
    .limit(50);
  if (error || !Array.isArray(data)) return [];
  return (data as TrackResultRow[]).map(parseTrackResultRow);
}

/** The newest result for each of several tracks (a cycle's tracks). */
export async function fetchCurrentTrackResults(trackIds: string[]): Promise<Map<string, TrackResultRecord>> {
  const out = new Map<string, TrackResultRecord>();
  const ids = [...new Set(trackIds.filter(Boolean))];
  const from = fromOf();
  if (!from || ids.length === 0) return out;
  const { data, error } = await from('track_results')
    .select(TRACK_RESULT_COLUMNS)
    .in('track_id', ids)
    .order('published_at', { ascending: false });
  if (error || !Array.isArray(data)) return out;
  for (const row of data as TrackResultRow[]) {
    if (!out.has(row.track_id)) out.set(row.track_id, parseTrackResultRow(row));
  }
  return out;
}

export function useCurrentTrackResult(trackId: string | null | undefined, refreshKey?: unknown) {
  const [record, setRecord] = useState<TrackResultRecord | null>(null);
  const [loading, setLoading] = useState(Boolean(trackId));
  useEffect(() => {
    if (!trackId) {
      setRecord(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void fetchCurrentTrackResult(trackId).then((r) => {
      if (cancelled) return;
      setRecord(r);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [trackId, refreshKey]);
  return { record, loading } as const;
}

// ── The desk: what a ball holder could publish ──────────────────────────────

export interface TrackResultCandidate {
  sessionId: string;
  sessionName: string | null;
  state: string;
  closedAt: string | null;
  sessionPublishedAt: string | null;
  itemCount: number;
  /** Ranked the same items as the newest closed session (combinable). */
  comparable: boolean;
  /** Already among the published result's sources. */
  inPublishedResult: boolean;
}

export interface TrackResultCandidates {
  trackId: string;
  strategy: TrackResultStrategy | null;
  writeBack: string | null;
  mayPublish: boolean;
  published: PublishedTrackResult | null;
  candidates: TrackResultCandidate[];
}

export async function fetchTrackResultCandidates(trackId: string): Promise<TrackResultCandidates | null> {
  const rpc = rpcOf();
  if (!rpc || !trackId) return null;
  const { data, error } = await rpc('get_track_result_candidates', { p_track_id: trackId });
  if (error || !data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const published = parsePublishedTrackResults([d.published])[0] ?? null;
  const list = Array.isArray(d.candidates) ? (d.candidates as Array<Record<string, unknown>>) : [];
  return {
    trackId,
    strategy: strategyOf(d.strategy),
    writeBack: typeof d.write_back === 'string' ? d.write_back : null,
    mayPublish: d.may_publish === true,
    published,
    candidates: list.map((c) => ({
      sessionId: String(c.session_id ?? ''),
      sessionName: typeof c.session_name === 'string' ? c.session_name : null,
      state: String(c.state ?? ''),
      closedAt: typeof c.closed_at === 'string' ? c.closed_at : null,
      sessionPublishedAt: typeof c.session_published_at === 'string' ? c.session_published_at : null,
      itemCount: Number(c.item_count ?? 0),
      comparable: c.comparable === true,
      inPublishedResult: c.in_published_result === true,
    })),
  };
}

export function useTrackResultCandidates(trackId: string | null | undefined, refreshKey?: unknown) {
  const [data, setData] = useState<TrackResultCandidates | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!trackId) return;
    let cancelled = false;
    void fetchTrackResultCandidates(trackId).then((d) => {
      if (!cancelled) setData(d);
    });
    return () => {
      cancelled = true;
    };
  }, [trackId, refreshKey, tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { data, refresh } as const;
}

// ── The act ─────────────────────────────────────────────────────────────────

export interface PublishTrackResultOutcome {
  ok: boolean;
  code?: string;
  error?: string;
  trackResultId?: string;
  strategy?: TrackResultStrategy | null;
  sourceSessionIds?: string[];
  itemCount?: number;
  leftOut?: Array<{ sessionId: string; sessionName: string | null; reason: string }>;
}

/**
 * Publish a result for the track — the ball holder's act (publish_track_result,
 * mig 934). With no session named the server publishes the newest closed one
 * (or the track's chosen one), or combines the comparable ones under the
 * aggregate rule. One transaction; the server's own reason comes back verbatim.
 */
export async function publishTrackResult(params: {
  trackId: string;
  sessionId?: string | null;
  note?: string | null;
  actorName?: string | null;
}): Promise<PublishTrackResultOutcome> {
  const rpc = rpcOf();
  if (!rpc) return { ok: false, code: 'offline', error: 'No connection to the server.' };
  const { data, error } = await rpc('publish_track_result', {
    p_track_id: params.trackId,
    p_session_id: params.sessionId ?? null,
    p_note: params.note ?? null,
    p_actor_name: params.actorName ?? null,
  });
  if (error) return { ok: false, code: 'rpc_error', error: error.message };
  const d = (data ?? {}) as Record<string, unknown>;
  if (d.success !== true) {
    return { ok: false, code: typeof d.code === 'string' ? d.code : 'refused', error: typeof d.error === 'string' ? d.error : 'The server refused to publish this result.' };
  }
  return {
    ok: true,
    trackResultId: typeof d.track_result_id === 'string' ? d.track_result_id : undefined,
    strategy: strategyOf(d.strategy),
    sourceSessionIds: uuidList(d.source_session_ids),
    itemCount: typeof d.item_count === 'number' ? d.item_count : undefined,
    leftOut: Array.isArray(d.left_out)
      ? (d.left_out as Array<Record<string, unknown>>).map((x) => ({
          sessionId: String(x.session_id ?? ''),
          sessionName: typeof x.session_name === 'string' ? x.session_name : null,
          reason: typeof x.reason === 'string' ? x.reason : '',
        }))
      : [],
  };
}
